<template>
  <div
    class="ifr-progress"
    :style="style"
    :class="{ indeterminate: indeterminate }"
  >
    <svg
      :width="finalWidth"
      :height="finalHeight"
      :viewBox="`0 0 ${finalWidth} ${finalHeight}`"
    >
      <g
        :transform="`translate(${(finalWidth - size) / 2}, ${
          (finalHeight - size) / 2
        }) rotate(${finalRotation}, ${size / 2}, ${size / 2})`"
      >
        <path
          ref="path"
          :style="{ stroke: fill, 'stroke-width': strokeWidth }"
          :d="path"
          :stroke-dasharray="`${finalDasharray} ${finalDasharray}`"
          :stroke-dashoffset="finalDashoffset"
        />
      </g>
    </svg>
  </div>
</template>

<script>
const shapes = {
  circle: (size) => `
  	M ${size / 2}, ${size / 2}
    m 0, -${size / 2}
    a ${size / 2},${size / 2} 0 1 1 0,${size}
    a ${size / 2},${size / 2} 0 1 1 0,-${size}
  `,
  semicircle: (size) => `
  	M ${size / 2}, ${size / 2}
    m -${size / 2}, 0
    a ${size / 2},${size / 2} 0 1 1 ${size},0
  `,
  line: (size) => `
  	M 0,${size / 2}
    L ${size},${size / 2}
  `,
};

export default {
  name: "IfrProgress",
  props: {
    counterClockwise: {
      type: Boolean,
      default: false,
    },
    fillDuration: {
      type: [String, Number],
      default: 1,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    progress: {
      type: Number,
      default: 0,
      validate(value) {
        return value >= -1 && value <= 1;
      },
    },
    rotate: {
      type: Boolean,
      default: false,
    },
    rotationDuration: {
      type: Number,
      default: 1,
    },
    shape: {
      type: String,
      default: null,
    },
    size: {
      type: Number,
      default: 100,
    },
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 0,
    },
    fill: {
      type: String,
      default: "#48738e",
    },
    strokeWidth: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      dasharray: 0,
      dashoffset: 0,
      rotation: 0,
      animating: false,
    };
  },
  computed: {
    finalDasharray() {
      return this.finalDashoffset === 0
        || this.finalDashoffset === this.dasharray * 2
        ? 0
        : this.dasharray;
    },
    finalDashoffset() {
      if (this.animating) {
        return this.dashoffset;
      }
      if (this.finalProgress < 0) {
        return -this.dasharray * this.finalProgress + this.dasharray;
      }
      return (1 - this.finalProgress) * this.dasharray;
    },
    finalProgress() {
      if (this.counterClockwise) {
        return -this.progress;
      }
      return this.progress;
    },
    finalRotation() {
      return this.animating ? this.rotation : 0;
    },
    finalWidth() {
      return this.width || this.size + this.size / 2;
    },
    finalHeight() {
      return this.height || this.size + this.size / 2;
    },
    style() {
      return {
        width: `${this.finalWidth}px`,
        height: `${this.finalHeight}px`,
      };
    },
    path() {
      if (!shapes[this.shape]) {
        throw new TypeError("[Progress.vue] No shape with key ", this.shape);
      } else return shapes[this.shape](this.size);
    },
  },
  watch: {
    indeterminate: {
      handler(value) {
        if (value) {
          this.startAnimation();
        } else {
          this.stopAnimation();
        }
      },
      immediate: true,
    },
    path: {
      handler() {
        this.$nextTick(() => {
          this.dasharray = this.$refs.path.getTotalLength();
        });
      },
      immediate: true,
    },
  },
  methods: {
    frame(timestamp) {
      const time = timestamp - this._animationLastFrame;
      this._animationLastFrame = timestamp;
      if (this.rotate) {
        const direction = this.counterClockwise ? -1 : 1;
        const d = (time * 360) / (parseFloat(this.rotationDuration) * 1000);
        this.rotation += d * direction;
        if (this.counterClockwise) {
          if (this.rotation < 0) {
            this.rotation += 360;
          }
        } else if (this.rotation > 360) {
          this.rotation -= 360;
        }
      }
      const range = this.dasharray * 2;
      const direction = this.counterClockwise ? 1 : -1;
      const d = (time * range) / (parseFloat(this.fillDuration) * 1000);
      this.dashoffset += d * direction;
      if (this.counterClockwise) {
        if (this.dashoffset < 0) {
          this.dashoffset += range;
        }
      } else if (this.dashoffset > range) {
        this.dashoffset -= range;
      }

      if (this.animating) {
        requestAnimationFrame(this.frame);
      }
    },
    startAnimation() {
      this.dashoffset = this.dasharray;
      this.rotation = 0;
      this.animating = true;
      this._animationLastFrame = performance.now();
      requestAnimationFrame(this.frame);
    },
    stopAnimation() {
      this.animating = false;
    },
  },
  beforeDestroy() {
    this.stopAnimation();
  },
};
</script>

<style lang="scss">
</style>
